import React from 'react';
import PropTypes from 'prop-types';
import { cn } from 'lib/utils';
import { Loader2 } from 'lucide-react';
import FullLogoAnimated from './FullLogoAnimated';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import useBrandLogo from 'hooks/useBrandLogo';
import { getCompany } from 'helpers/getCompanyVariables';

const company = getCompany();
const isOneill = company === 'oneill';
const isSunshine = company === 'sunshine';

const baseClasses =
  'flex justify-center items-center overflow-hidden bg-[rgba(247,249,251,0.85)]';

/** Covers the MainLayout content column (sidebar excluded), not scroll position. */
const localClasses = 'absolute inset-0 z-[100]';
const globalClasses = 'fixed inset-0 w-screen h-screen z-[1000]';

function Loader({ global: isGlobal, className }) {
  const { hasKnownBrand } = useBrandLogo();

  const logoSizes = isGlobal
    ? 'w-[500px] max-md:w-[250px]'
    : 'w-[400px] max-md:w-[250px]';

  // an agency logo can be any aspect ratio, so it is capped on both axes instead
  // of being stretched to the fixed width the bundled square mark assumes
  const agencyLogoSizes = isGlobal
    ? 'w-auto max-w-[500px] max-h-[220px] max-md:max-w-[250px] max-md:max-h-[130px]'
    : 'w-auto max-w-[400px] max-h-[180px] max-md:max-w-[250px] max-md:max-h-[130px]';

  // the spinner only stands in on the very first sign-in on this browser, when there
  // is no brand to be sure of yet. after that there is always one to paint: the
  // agency logo, or the bundled sunshine one when the agency has none of its own
  const showsSpinner = isSunshine && !hasKnownBrand;
  const showsLogo = isSunshine && !showsSpinner;

  return (
    <div
      className={cn(
        baseClasses,
        isGlobal ? globalClasses : localClasses,
        className
      )}
    >
      <div
        className={cn(
          isOneill && [
            'animate-loader-pulse [&_svg]:block [&_svg]:mx-auto [&_svg]:w-full',
            logoSizes
          ],
          showsLogo && 'animate-loader-pulse'
        )}
      >
        {isOneill && <FullLogoAnimated />}
        {showsLogo && (
          <BrandLogo
            className={logoSizes}
            agencyClassName={agencyLogoSizes}
            alt=''
          />
        )}
        {showsSpinner && (
          <Loader2
            aria-hidden
            className={cn(
              'shrink-0 animate-spin text-primary',
              isGlobal ? 'size-16' : 'size-12'
            )}
          />
        )}
      </div>
    </div>
  );
}

Loader.propTypes = {
  global: PropTypes.bool,
  className: PropTypes.string
};

export default Loader;
